import React, { useState } from 'react';
import PropTypes from 'prop-types';

import GridDropDown from '../../ui/dropdown/GridDropdown';
import ValidationText from '../../ui/validation/ValidationText';

/**
 * EditDropdownColumn component for rendering a dropdown with editable functionality.
 * @param {object} props - Component props.
 * @param {object} props.rowData - Data for the current row.
 * @param {object} props.col - Column configuration.
 * @param {object} props.editColumn - Editable column configuration.
 * @param {number} props.rowIndex - Index of the current row.
 * @param {function} props.onChange - Function to handle changes.
 * @param {object} props.updatedData - Updated row data.
 * @param {object} props.errors - Validation errors.
 */
const EditDropdownColumn = ({
  rowData,
  col,
  editColumn,
  rowIndex,
  onChange,
  updatedData,
  errors,
}) => {

  const [value, setValue] = useState(rowData[editColumn.editColFieldName]);

  const { isMultiSelect, options, isDisable, placeholder } = editColumn;

  /**
   * Handles change in dropdown selection.
   * @param {object|string} selectedOption - The selected option.
   */
  const handleOnChange = (selectedOption) => {
    let selectedValue;

    if (typeof selectedOption === 'object' && selectedOption !== null && 'value' in selectedOption) {
      selectedValue = selectedOption.value;
    } else {
      selectedValue = selectedOption;
    }

    setValue(selectedValue);

    if (onChange) {

      const newRowData = updatedData
        ? {
          ...updatedData,
          [col.fieldName]: selectedOption.label,
          [editColumn.editColFieldName]: selectedOption.value
        }
        : {
          ...rowData,
          [col.fieldName]: selectedOption.label,
          [editColumn.editColFieldName]: selectedOption.value
        };
      onChange(rowIndex, newRowData, col?.fieldName);
    }
  };

  const hasError = !!errors?.[rowIndex]?.[col.fieldName];
  const errorMessage = errors?.[rowIndex]?.[col.fieldName] || '';

  return (
    <>
      <GridDropDown
        placeholder={placeholder}
        isMultiSelect={isMultiSelect}
        options={options}
        value={value}
        onChange={handleOnChange}
        cssClass={`${hasError ? 'error' : ''}`}
        isDisabled={isDisable}
        hasError={hasError}
      />
      {hasError && <ValidationText error={errorMessage || ' '} className="error-message" />}
    </>
  );
};

EditDropdownColumn.propTypes = {
  rowData: PropTypes.object.isRequired,
  col: PropTypes.shape({
    fieldName: PropTypes.string.isRequired,
  }).isRequired,
  editColumn: PropTypes.shape({
    isMultiSelect: PropTypes.bool,
    options: PropTypes.array.isRequired,
    isDisable: PropTypes.bool,
    placeholder: PropTypes.string,
    editColFieldName: PropTypes.string.isRequired,
  }).isRequired,
  rowIndex: PropTypes.number.isRequired,
  onChange: PropTypes.func.isRequired,
  updatedData: PropTypes.object,
  errors: PropTypes.object,
};

export default EditDropdownColumn;